import { initializeApp } from 'firebase/app';
import { 
  getAuth,
  PhoneAuthProvider, 
  signInWithPhoneNumber,
  signOut,
  RecaptchaVerifier,
  ConfirmationResult
} from 'firebase/auth';
import { 
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  FirestoreError,
  enableNetwork,
  disableNetwork
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
    apiKey: "AIzaSyC_Li1dPfdh33ZeP4fverjQCZKYmeA86s4",
    authDomain: "strange-firefly-440418-k8.firebaseapp.com",
    projectId: "strange-firefly-440418-k8",
    storageBucket: "strange-firefly-440418-k8.firebasestorage.app",
    messagingSenderId: "329105748025",
    appId: "1:329105748025:web:8eac4038ba2802cad29de8",
    measurementId: "G-SHNZ29M7QM"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Initialize Firestore with the new persistence configuration
const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager()
  })
});
// Initialize Storage
const storage = getStorage(app);

// Network state management
let isNetworkEnabled = true;
const networkStateListeners: ((online: boolean) => void)[] = [];

export const addNetworkStateListener = (listener: (online: boolean) => void) => {
  networkStateListeners.push(listener);
};

export const removeNetworkStateListener = (listener: (online: boolean) => void) => {
  const index = networkStateListeners.indexOf(listener);
  if (index > -1) {
    networkStateListeners.splice(index, 1);
  }
};

const updateNetworkState = async (online: boolean) => {
  if (online === isNetworkEnabled) return;
  
  try {
    if (online) {
      await enableNetwork(db);
    } else {
      await disableNetwork(db);
    }
    isNetworkEnabled = online;
    networkStateListeners.forEach(listener => listener(online));
  } catch (error) {
    console.error('Error updating network state:', error);
  }
};

// Initialize network state listeners
window.addEventListener('online', () => updateNetworkState(true));
window.addEventListener('offline', () => updateNetworkState(false));

// Initial network state check
updateNetworkState(navigator.onLine);

const phoneAuth: PhoneAuthInterface = {
  provider: new PhoneAuthProvider(auth),
  
  sendVerificationCode: async (phoneNumber: string, recaptchaVerifier: RecaptchaVerifier) => {
    try {
      return await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
    } catch (error) {
      console.error('Error sending verification code:', error);
      throw error;
    }
  },
  
  signOut: async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }
};

export const handleFirebaseError = (error: FirestoreError | unknown): string => {
  if ((error as FirestoreError).code) {
    const firestoreError = error as FirestoreError;
    switch (firestoreError.code) {
      case 'failed-precondition':
        return 'Multiple tabs open. Please use only one tab.';
      case 'unimplemented':
        return 'The browser doesn\'t support all required features';
      case 'unavailable':
        return 'The service is currently unavailable. Please check your internet connection.';
      default:
        return `An error occurred: ${firestoreError.message}`;
    }
  }
  return 'An unexpected error occurred';
};

interface PhoneAuthInterface {
  provider: PhoneAuthProvider;
  sendVerificationCode: (phoneNumber: string, recaptchaVerifier: RecaptchaVerifier) => Promise<ConfirmationResult>;
  signOut: () => Promise<void>;
}

export { 
  app, 
  auth, 
  db, 
  phoneAuth,
  storage
};
